import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquareLock, ScanSearch, ShieldAlert, Lock, CheckCircle2, Loader2, ArrowRight, Activity,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { ActionBadge, Card, Section, Pill, Btn, StatCard, RequestDetailDrawer } from "./ui";

const ACTION_HINT = {
  BLOCK: "Blocked — this content was not sent to any AI provider.",
  MASK: "Masked — sensitive tokens were shielded before transit.",
  WARN: "Allowed with a logged warning.",
  ALLOW: "Allowed — no sensitive data detected.",
};

export default function EmployeeDashboard() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const tid = session.tenant.id;
  const sector = session.tenant.sector;
  const [rows, setRows] = useState(null);
  const [pack, setPack] = useState(null);
  const [selected, setSelected] = useState(null);

  const continueInChat = (text) => {
    sessionStorage.setItem("lg_prefill", text || "");
    navigate("/chat");
  };

  useEffect(() => {
    api.audit(tid, { user_id: session.user.id, limit: 50 }).then(setRows);
    api.sectorPacks().then((d) => setPack(d.packs.find((p) => p.id === sector) || null));
  }, [tid, sector, session.user.id]);

  const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { hour12: false });
  const total = rows?.length || 0;
  const blocked = (rows || []).filter((r) => r.decision === "BLOCK").length;
  const masked = (rows || []).filter((r) => r.masked_count > 0).length;

  return (
    <Section
      title={`Welcome, ${session.user.name.split(" ")[0]}`}
      subtitle="Your personal AI usage overview — every prompt is checked before it reaches a provider."
      right={
        <Btn data-testid="employee-open-chat" onClick={() => navigate("/chat")}>
          <MessageSquareLock className="h-4 w-4" /> Open Gateway Chat
        </Btn>
      }
    >
      {/* Sector context — driven entirely by the tenant's active sector pack */}
      <Card className="mb-6 p-5" data-testid="employee-sector-context">
        <div className="flex items-center gap-2">
          <ScanSearch className="h-4 w-4 text-brand" />
          <h3 className="font-display text-sm font-bold">{pack ? pack.label : "Sector Pack"}</h3>
          <Pill tone="eu">{session.tenant.jurisdiction?.split("·").pop()?.trim()}</Pill>
        </div>
        <p className="mt-1 text-[11px] text-slate-500">
          {pack ? pack.description : "Loading your firm's governance baseline…"} These are the categories
          LucidGate watches for on your prompts.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {pack ? pack.categories.map((c) => (
            <span key={c.id} data-testid="employee-watched-category" className="flex items-center gap-1.5 rounded-lg border border-subtle bg-elevated px-2.5 py-1.5 text-xs text-slate-300">
              <ShieldAlert className="h-3 w-3 text-warn" /> {c.label}
              <ActionBadge action={c.default_action} small />
            </span>
          )) : <Loader2 className="h-4 w-4 animate-spin text-brand" />}
        </div>
      </Card>

      {/* Personal stats */}
      <div className="mb-6 grid grid-cols-3 gap-4">
        <StatCard icon={Activity} label="Your requests" value={total} color="#0EA5E9" testid="employee-stat-total" />
        <StatCard icon={ShieldAlert} label="Blocked" value={blocked} color="#F43F5E" valueColor="#F43F5E" testid="employee-stat-blocked" />
        <StatCard icon={Lock} label="Masked" value={masked} color="#06B6D4" valueColor="#06B6D4" testid="employee-stat-masked" />
      </div>

      {/* Recent requests */}
      <h3 className="mb-3 font-display text-sm font-bold">Your recent requests</h3>
      {!rows ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : rows.length === 0 ? (
        <Card className="flex h-48 flex-col items-center justify-center gap-3 text-center text-slate-500" data-testid="employee-empty">
          <MessageSquareLock className="h-7 w-7" />
          <p className="max-w-sm text-sm">You haven't sent any prompts yet. Head to Gateway Chat to get started safely.</p>
          <Btn variant="ghost" data-testid="employee-empty-chat" onClick={() => navigate("/chat")}>
            Open Gateway Chat <ArrowRight className="h-4 w-4" />
          </Btn>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="employee-recent-list">
          {rows.slice(0, 10).map((r) => (
            <Card
              key={r.id}
              className="cursor-pointer p-4 transition-colors hover:border-highlight hover:bg-white/5"
              data-testid="employee-request-row"
              onClick={() => setSelected(r)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-200">{r.prompt_preview}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {r.category_labels.length ? r.category_labels.map((c) => (
                      <span key={c} className="rounded bg-warn/10 px-1.5 py-0.5 text-[10px] text-warn">{c}</span>
                    )) : (
                      <span className="flex items-center gap-1 text-[11px] text-allow"><CheckCircle2 className="h-3 w-3" /> clean</span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <ActionBadge action={r.decision} small />
                  <div className="mt-1 font-mono text-[10px] text-slate-500">{fmt(r.timestamp)}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2 border-t border-subtle/50 pt-2 text-[11px] text-slate-400">
                <span className="min-w-0 truncate">
                  {ACTION_HINT[r.decision] || r.routing_reason}
                  {r.provider_name && <span className="text-slate-600"> · routed to {r.provider_name}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-brand">
                  View trace <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Card>
          ))}
          {rows.length > 10 && (
            <p className="pt-1 text-center font-mono text-[11px] text-slate-500" data-testid="employee-more-hint">
              Showing your latest 10 of {rows.length} requests.
            </p>
          )}
        </div>
      )}

      {selected && (
        <RequestDetailDrawer
          row={selected}
          onClose={() => setSelected(null)}
          onContinue={(text) => continueInChat(text)}
        />
      )}
    </Section>
  );
}
